import { useState, useMemo, useRef } from 'react'
import { format, parseISO } from 'date-fns'
import { useAuth } from '../../../hooks/useAuth'
import { useEventMutations } from '../hooks/useCalendarEvents'
import { useEventPhotos } from '../hooks/useEventPhotos'
import { LightboxViewer } from '../../../components/LightboxViewer'
import { getSignedUrl } from '../../../lib/upload'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'

const formatDt = (iso, allDay) => {
  if (!iso) return ''
  return allDay ? format(parseISO(iso), 'MMM d, yyyy') : format(parseISO(iso), 'MMM d, yyyy h:mm a')
}

// ── Photo grid ─────────────────────────────────────────────────
const PhotoGrid = ({ photos, userId, isAdmin, onPhotoClick, onDelete, onEditCaption }) => {
  const [thumbUrls, setThumbUrls] = useState({})

  const loadThumb = async (photo) => {
    if (thumbUrls[photo.id]) return
    try {
      const thumbPath = photo.file_path.replace(/(\.[^.]+)?$/, '_thumb.jpg')
      const url = await getSignedUrl('household-photos', thumbPath)
      setThumbUrls((prev) => ({ ...prev, [photo.id]: url }))
    } catch {
      // Fall back to full image URL
      try {
        const url = await getSignedUrl('household-photos', photo.file_path)
        setThumbUrls((prev) => ({ ...prev, [photo.id]: url }))
      } catch {}
    }
  }

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1">
      {photos.map((photo, i) => {
        if (!thumbUrls[photo.id]) loadThumb(photo)
        return (
          <button
            key={photo.id}
            onClick={() => onPhotoClick(i)}
            className="aspect-square rounded-lg overflow-hidden bg-border/30 relative group"
          >
            {thumbUrls[photo.id] ? (
              <img
                src={thumbUrls[photo.id]}
                alt={photo.file_name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {!navigator.onLine ? (
                  <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z"/>
                  </svg>
                ) : (
                  <div className="w-5 h-5 rounded-full border-2 border-border border-t-primary animate-spin" />
                )}
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ── Upload progress grid ───────────────────────────────────────
const UploadingGrid = ({ count }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1 mt-2">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="aspect-square rounded-lg bg-border/30 flex items-center justify-center">
        <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    ))}
  </div>
)

export const EventDetail = ({ event, members, tasks, onEdit, onClose }) => {
  const { user, household, isAdmin, isLimited } = useAuth()
  const { deleteEvent } = useEventMutations()
  const { data: photos = [], isLoading: photosLoading, addPhotos, updateCaption, removePhoto } = useEventPhotos(event._originalId ?? event.id)

  const [tab,           setTab]           = useState('details')
  const [deleting,      setDeleting]      = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(null)
  const [uploading,     setUploading]     = useState(0)
  const fileInputRef = useRef(null)

  const memberMap = useMemo(() => {
    const map = {}
    for (const m of members ?? []) {
      if (m.profiles) map[m.profiles.id] = m.profiles
    }
    return map
  }, [members])

  const taggedMembers = useMemo(() =>
    (event.event_members ?? []).map((em) => memberMap[em.user_id]).filter(Boolean),
    [event, memberMap],
  )

  const driver = event.driver_id ? memberMap[event.driver_id] : null

  const linkedTasks = useMemo(() =>
    (tasks ?? []).filter((t) => t.event_id === event._originalId || t.event_id === event.id),
    [tasks, event],
  )

  const canEdit = user?.id === event.created_by || household?.role === 'admin' || isAdmin

  const handleDelete = async () => {
    setDeleting(true)
    try {
      const targetId = event._originalId ?? event.id
      await deleteEvent(targetId)
      onClose()
    } catch (err) {
      console.error(err)
      setDeleting(false)
    }
  }

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files ?? []).slice(0, 20)
    if (!files.length) return
    setUploading(files.length)
    try {
      await addPhotos.mutateAsync(files)
    } catch (err) {
      console.error(err)
    } finally {
      setUploading(0)
      e.target.value = ''
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
        <div className="absolute inset-0 bg-black/40" />
        <div
          className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[85dvh] flex flex-col shadow-card"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Color bar */}
          <div
            className="h-1.5 rounded-t-2xl flex-shrink-0"
            style={{ backgroundColor: event.calendars?.color ?? '#2d1b5e' }}
          />

          <div className="flex items-start justify-between px-5 pt-4 pb-2 flex-shrink-0">
            <h2 className="text-lg font-bold text-text-primary pr-4 leading-tight">{event.title}</h2>
            <button onClick={onClose} className="text-text-muted hover:text-text-primary flex-shrink-0 p-1">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 px-5 border-b border-border flex-shrink-0">
            <button
              onClick={() => setTab('details')}
              className={cn('text-sm font-medium pb-2.5 border-b-2 -mb-px transition-colors',
                tab === 'details' ? 'border-primary text-primary' : 'border-transparent text-text-muted')}
            >
              Details
            </button>
            <button
              onClick={() => setTab('photos')}
              className={cn('text-sm font-medium pb-2.5 border-b-2 -mb-px transition-colors flex items-center gap-1.5',
                tab === 'photos' ? 'border-primary text-primary' : 'border-transparent text-text-muted')}
            >
              Photos
              {photos.length > 0 && (
                <span className={cn('text-xs px-1.5 py-0.5 rounded-full font-semibold',
                  tab === 'photos' ? 'bg-primary text-white' : 'bg-border text-text-muted')}>
                  {photos.length}
                </span>
              )}
            </button>
          </div>

          {/* Tab content */}
          <div className="overflow-y-auto flex-1 min-h-0 px-5 pb-4">
            {tab === 'details' && (
              <div className="space-y-3 pt-3">
                {/* Calendar */}
                {event.calendars && (
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: event.calendars.color }} />
                    {event.calendars.name}
                  </div>
                )}
                {/* Time */}
                <div className="text-sm text-text-secondary">
                  {event.is_all_day ? (
                    <span>All day · {formatDt(event.start_time, true)}</span>
                  ) : (
                    <span>
                      {formatDt(event.start_time, false)}
                      {event.end_time && ` – ${formatDt(event.end_time, false)}`}
                    </span>
                  )}
                </div>
                {/* Location */}
                {event.location && (
                  <div className="text-sm text-text-secondary flex items-start gap-1.5">
                    <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                      <circle cx="12" cy="9" r="2.5" />
                    </svg>
                    {event.location}
                  </div>
                )}
                {/* Description */}
                {event.description && (
                  <p className="text-sm text-text-secondary whitespace-pre-wrap">{event.description}</p>
                )}
                {/* Tagged members */}
                {taggedMembers.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-text-muted mb-1.5 uppercase tracking-wide">Members</div>
                    <div className="flex gap-1.5 flex-wrap">
                      {taggedMembers.map((p) => (
                        <span key={p.id} className="text-xs font-medium px-2.5 py-1 rounded-full text-white"
                          style={{ backgroundColor: p.member_color ?? '#7c6faf' }}>
                          {p.display_name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Driver */}
                {driver && (
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <svg className="w-4 h-4 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="3" width="15" height="13" rx="2" />
                      <path d="m16 8 5 3v5h-5" />
                      <circle cx="5.5" cy="18.5" r="2.5" />
                      <circle cx="18.5" cy="18.5" r="2.5" />
                    </svg>
                    <span>Driver: <strong>{driver.display_name}</strong></span>
                  </div>
                )}
                {/* Linked tasks */}
                {linkedTasks.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-text-muted mb-1.5 uppercase tracking-wide">Linked Tasks</div>
                    <div className="space-y-1">
                      {linkedTasks.map((t) => (
                        <div key={t.id} className={cn('text-sm flex items-center gap-2', t.completed_at && 'line-through text-text-muted')}>
                          <span className={cn('w-3 h-3 rounded-sm border', t.completed_at ? 'bg-emerald-400 border-emerald-400' : 'border-border')} />
                          {t.title}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {tab === 'photos' && (
              <div className="pt-3">
                {!isLimited && (
                  <div className="flex justify-end mb-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={handlePhotoUpload}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1.5 text-sm text-primary font-medium"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                        <circle cx="12" cy="13" r="3"/>
                      </svg>
                      Add photos
                    </button>
                  </div>
                )}

                {photosLoading && <div className="text-sm text-text-muted">Loading…</div>}

                {!photosLoading && photos.length === 0 && uploading === 0 && (
                  <div className="text-center py-8 text-text-muted">
                    <svg className="w-10 h-10 mx-auto mb-2 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                    </svg>
                    <p className="text-sm">No photos yet.</p>
                  </div>
                )}

                {photos.length > 0 && (
                  <PhotoGrid
                    photos={photos}
                    userId={user?.id}
                    isAdmin={isAdmin}
                    onPhotoClick={(i) => setLightboxIndex(i)}
                    onDelete={(id) => removePhoto.mutate({ id, filePath: photos.find((p) => p.id === id)?.file_path })}
                    onEditCaption={(id, caption) => updateCaption.mutate({ id, caption })}
                  />
                )}

                {uploading > 0 && <UploadingGrid count={uploading} />}
              </div>
            )}
          </div>

          {/* Actions */}
          {canEdit && (
            <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
              {confirmDelete ? (
                <>
                  <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(false)} className="flex-1">Cancel</Button>
                  <Button size="sm" className="flex-1 bg-rose-500 hover:bg-rose-600 text-white border-rose-500"
                    onClick={handleDelete} disabled={deleting}>
                    {deleting ? <Spinner size="sm" /> : 'Confirm Delete'}
                  </Button>
                </>
              ) : (
                <>
                  <Button size="sm" className="flex-1 bg-rose-500 hover:bg-rose-600 text-white border-rose-500"
                    onClick={() => setConfirmDelete(true)}>Delete</Button>
                  <Button variant="secondary" size="sm" className="flex-1" onClick={() => onEdit(event)}>Edit</Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <LightboxViewer
          photos={photos}
          initialIndex={lightboxIndex}
          bucket="household-photos"
          userId={user?.id}
          isAdmin={isAdmin}
          onClose={() => setLightboxIndex(null)}
          onDelete={(id) => removePhoto.mutateAsync({ id, filePath: photos.find((p) => p.id === id)?.file_path })}
          onEditCaption={(id, caption) => updateCaption.mutateAsync({ id, caption })}
        />
      )}
    </>
  )
}
